import { memo, useRef } from 'react';

import { useBatchedMeshLifecycleHandler } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useBatchedMeshLifecycleHandler';
import { useBatchUpdateHandler } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useBatchUpdateHandler';
import { useDraggingLifecycleHandler } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useDraggingLifecycleHandler';
import { useInstanceMatrixSyncHandler } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useInstanceMatrixSyncHandler';
import { useInstanceSyncHandler } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useInstanceSyncHandler';
import { useSelectionHandler } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useSelectionHandler';
import { cloneArticleInstances } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { PrefabModel } from 'api/models';
import type { ArticleMesh } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleMesh';
import type { ArticleReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleReference';

interface ArticlePlacementHandlerProps {
    articleId: string;
    isMainCanvas: boolean;
    articleRepresentation: PrefabModel;
    articleMeshes: ArticleMesh[];
}

/**
 * Responsibilities:
 * - Manages the lifecycle of article instances for a specific article type.
 * - Registers and removes geometry slots and instances in the BatchedMesh when the placement store changes.
 * - Handles matrix and instance synchronization via Zustand subscriptions and hooks.
 * - Registers drag references for event handling and selection logic.
 * - Coordinates selection, batch updates, and dragging lifecycle for article instances.
 * - Disposes Three.js geometry and material resources on unmount to prevent memory leaks.
 *
 * Note: This component does not render any scene objects - this is done by the BatchedMeshHandlers.
 */
export const ArticlePlacementHandler = memo(
    ({ articleId, isMainCanvas, articleRepresentation, articleMeshes }: ArticlePlacementHandlerProps) => {
        const articleRef = useRef<ArticleReference>(
            (() => {
                const article = usePlacementStore.getState().articles[articleId];
                return {
                    id: articleId,
                    isMainCanvas,
                    articleNumber: article.articleNumber,
                    lifeCycleCode: article.lifeCycleCode,
                    articleRepresentations: [articleRepresentation],
                    articleInstances: cloneArticleInstances(article),
                    edgeGeometries: article.edgeGeometries
                };
            })()
        );

        const instanceSelectionIdsRef = useRef<string[]>(
            Object.keys(usePlacementStore.getState().articleInstanceSelections)
        );
        const pendingColliderIdsRef = useRef<Set<string> | null>(null);
        const needsBatchMatrixUpdateRef = useRef<boolean>(false);
        const chunkUpdateInProgressRef = useRef<boolean>(false);
        const chunkUpdateIdRef = useRef<number>(0);
        const pendingUpdateIdsRef = useRef<Set<string> | null>(null);

        useDraggingLifecycleHandler({
            articleId,
            isMainCanvas,
            articleRef
        });

        useInstanceSyncHandler({
            isMainCanvas,
            articleRef,
            instanceSelectionIdsRef,
            chunkUpdateInProgressRef,
            chunkUpdateIdRef
        });

        useInstanceMatrixSyncHandler({
            articleRef,
            instanceSelectionIdsRef,
            needsBatchMatrixUpdateRef,
            chunkUpdateInProgressRef,
            chunkUpdateIdRef,
            pendingUpdateIdsRef
        });

        useBatchUpdateHandler({
            isMainCanvas,
            articleRef,
            pendingColliderIdsRef,
            needsBatchMatrixUpdateRef,
            chunkUpdateInProgressRef,
            chunkUpdateIdRef,
            pendingUpdateIdsRef
        });

        useBatchedMeshLifecycleHandler({
            articleId,
            articleMeshes,
            isMainCanvas,
            articleRef,
            pendingColliderIdsRef
        });

        useSelectionHandler({
            articleRef,
            isMainCanvas,
            instanceSelectionIdsRef
        });

        return null;
    }
);

ArticlePlacementHandler.displayName = 'ArticlePlacementHandler';
